import { manageAccount, stopManaging } from "@/admin/manageAccount";
import { adminNavItems } from "@/admin/navigation/adminNavConfig";
import { useAccountStore } from "@/composables/account/useAccountStore";
import {
  isAdminAccount,
  loadAccountForGate,
} from "@/composables/useAdminCheck";
import {
  useCommandPalette,
  type Command,
} from "@/composables/useCommandPalette";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Account, Company } from "@/types";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, shallowRef, watch } from "vue";
import { useRouter } from "vue-router";

/** Registers the palette's commands on mount; `isAdmin` also gates RootLayout's admin palette and nav. */
export function useCommandPaletteCommands() {
  const router = useRouter();
  const { registerCommands } = useCommandPalette();
  const { isManagingAccount } = storeToRefs(useAccountStore());

  // The admin palette and nav show once the account has loaded as an admin's; a failed load leaves
  // them hidden.
  const gateAccount = shallowRef<Readonly<Account> | null>(null);
  const isAdmin = computed(() => isAdminAccount(gateAccount.value));

  const adminAccounts = ref<Account[]>([]);

  // Navigation commands available to all users
  const navigationCommands: Command[] = [
    {
      id: "nav-dashboard",
      label: "Dashboard",
      group: "Navigation",
      icon: "chart-line",
      keywords: ["home", "overview"],
      action: () => router.push({ name: "Dashboard" }),
    },
    {
      id: "nav-market-insights",
      label: "Market Insights",
      group: "Navigation",
      icon: "chart-pie",
      keywords: ["prices", "market"],
      action: () => router.push({ name: "MarketInsights" }),
    },
    {
      id: "nav-ecolabels",
      label: "Ecolabels",
      group: "Navigation",
      icon: "leaf",
      keywords: ["green", "labels", "eco"],
      action: () => router.push({ name: "Ecolabels" }),
    },
    {
      id: "nav-settings",
      label: "Settings",
      group: "Navigation",
      icon: "cog",
      keywords: ["preferences", "profile"],
      action: () => router.push({ name: "Settings" }),
    },
  ];

  // Derived from the shared admin nav config so the palette, the admin nav
  // bar and the /admin hub always list the same destinations.
  const adminNavCommands: Command[] = [
    {
      id: "admin-index",
      label: "Admin Home",
      group: "Admin",
      icon: "building",
      action: () => router.push({ name: "AdminIndex" }),
    },
    ...adminNavItems.map((item): Command => ({
      id: item.id,
      label: item.label,
      group: "Admin",
      icon: item.icon,
      keywords: item.keywords,
      action: () => router.push({ name: item.routeName, query: item.query }),
    })),
  ];

  // Admin action commands
  function getActionCommands(): Command[] {
    const actions: Command[] = [];

    if (isManagingAccount.value) {
      actions.push({
        id: "action-return-to-admin",
        label: "Return to Admin",
        group: "Actions",
        icon: "arrow-left",
        keywords: ["stop", "managing", "back"],
        action: async () => {
          await stopManaging();
          router.push({ name: "AdminAccountsView" });
        },
      });
    }

    return actions;
  }

  function getCompanyCommands(): Command[] {
    const commands: Command[] = [];

    for (const account of adminAccounts.value) {
      for (const company of account.companies ?? []) {
        if (company.deleted_at) continue;

        commands.push(
          toManageCommand(account, company, async () => {
            await manageAccount(account.id!);
            router.push({
              name: "Root",
              query: {
                company_id: String(company.id),
                consolidated: "false",
              },
            });
          })
        );
      }
    }

    return commands;
  }

  function buildAllCommands() {
    const all: Command[] = [...navigationCommands, ...getActionCommands()];

    if (isAdmin.value) {
      all.push(...adminNavCommands);
      all.push(...getCompanyCommands());
    }

    registerCommands(all);
  }

  // Fetch admin accounts for manage-account switching
  async function fetchAdminAccounts() {
    try {
      const response = await goSolidApi.get("/api/admin/accounts");
      if (response.status === 200 && response.data) {
        adminAccounts.value = response.data;
      }
    } catch {
      // Silently fail — accounts won't appear in palette
    }
  }

  onMounted(async () => {
    // Neither rejects, so the non-admin commands are built whatever happens.
    gateAccount.value = await loadAccountForGate();
    if (isAdmin.value) await fetchAdminAccounts();
    buildAllCommands();
  });

  // Rebuild commands when managing state changes
  watch(isManagingAccount, () => {
    buildAllCommands();
  });

  return {
    isAdmin,
  };
}

/**
 * Maps one account-company pair to its palette entry. The pair — not the account — is the unit,
 * so a single entry highlights when an account owns several companies.
 *
 * The label's "Manage:" prefix and "(ID: ... - ...)" scaffolding are identical on every account
 * entry, so indexing the label would let any query built from those characters match all of them.
 * `searchText` narrows the primary match to the company name; everything else the label displays
 * stays reachable through `keywords`.
 */
export function toManageCommand(
  account: Account,
  company: Company,
  onSelect: () => Promise<void>
): Command {
  return {
    id: `manage-company-${company.id}-account-${account.id}`,
    label: `Manage: ${company.name} (ID: ${company.id} - ${account.email})`,
    searchText: company.name,
    group: "Accounts",
    icon: "building",
    keywords: [
      String(company.id),
      company.registry_code ?? "",
      account.email ?? "",
      account.name ?? "",
    ].filter(Boolean),
    action: onSelect,
  };
}
